import { cn } from '../../../lib/utils';
import { mmss } from './mtvSteps';

const SECTION = {
  Chorus: 'bg-amber-500/25 text-amber-100 border-amber-400/40',
  Verse: 'bg-neutral-800 text-neutral-300 border-neutral-700',
  Bridge: 'bg-sky-500/20 text-sky-100 border-sky-400/40',
};

/** Song timeline: waveform, sections, lyric lines and storyboard shots lined up on the song clock. */
export function SongTimeline({ analysis, lines = [], scenes = [], time = 0, onSeek }) {
  const total = analysis?.seconds || 0;
  if (!total) return null;
  const pct = (t) => `${Math.max(0, Math.min(100, (t / total) * 100))}%`;
  const peaks = analysis.peaks || [];
  const shots = scenes.filter((s) => s.song_start != null);
  const seek = (e) => { const r = e.currentTarget.getBoundingClientRect(); onSeek?.(((e.clientX - r.left) / r.width) * total); };
  return (
    <div className="space-y-1" data-testid="mtv-song-timeline">
      <div className="relative h-5">
        {(analysis.sections || []).map((s, i) => (
          <div key={i} title={`${s.label} · ${mmss(s.start)}–${mmss(s.end)}`} style={{ left: pct(s.start), width: pct(s.end - s.start) }}
            className={cn('absolute top-0 h-full overflow-hidden truncate rounded-sm border px-1 text-[9px] leading-[18px]', SECTION[s.label] || 'border-neutral-800 bg-neutral-900 text-neutral-500')}
            data-testid={`mtv-section-${i}`}>{s.label}</div>
        ))}
      </div>
      <div className="relative h-14 cursor-pointer overflow-hidden rounded-md bg-neutral-950 ring-1 ring-neutral-800" onClick={seek} data-testid="mtv-timeline-wave">
        <svg viewBox={`0 0 ${peaks.length || 1} 100`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
          {peaks.map((p, i) => (
            <rect key={i} x={i} y={50 - p * 46} width={0.65} height={Math.max(1, p * 92)}
              className={(i / peaks.length) * total <= time ? 'fill-amber-400' : 'fill-neutral-600'} />
          ))}
        </svg>
        <div className="absolute inset-y-0 w-px bg-amber-100 shadow-[0_0_6px_rgba(251,191,36,0.9)]" style={{ left: pct(time) }} />
      </div>
      <div className="relative h-2.5" data-testid="mtv-timeline-lyrics">
        {lines.map((ln, i) => (
          <div key={i} title={`${mmss(ln.start)} · ${ln.text}`} style={{ left: pct(ln.start), width: pct(Math.max(0.4, ln.end - ln.start)) }}
            className="absolute top-0 h-full rounded-sm bg-emerald-400/45" />
        ))}
      </div>
      {shots.length > 0 && (
        <div className="relative h-3" data-testid="mtv-timeline-shots">
          {shots.map((s, i) => (
            <div key={s.id} title={s.title || `Shot ${i + 1}`} style={{ left: pct(s.song_start), width: pct(s.duration_seconds || 1) }}
              className={cn('absolute top-0 h-full rounded-sm border border-neutral-950', s.video_url ? 'bg-amber-300/70' : 'bg-neutral-600')} />
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-3 pt-0.5 text-[10px] text-neutral-500">
        <span className="font-mono text-neutral-300" data-testid="mtv-timeline-clock">{mmss(time)} / {mmss(total)}</span>
        <span><i className="mr-1 inline-block h-2 w-2 rounded-sm bg-emerald-400/60" />lyric lines</span>
        {shots.length > 0 && <span><i className="mr-1 inline-block h-2 w-2 rounded-sm bg-amber-300/70" />shots (filled = filmed)</span>}
        {analysis.bpm ? <span>{Math.round(analysis.bpm)} BPM · {(analysis.beats || []).length} beats</span> : null}
      </div>
    </div>
  );
}
